import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Bold, Italic, Heading1, Heading2, Heading3, List, ListOrdered, Minus, Link2, Undo2, Redo2, Pilcrow } from 'lucide-react';

const Btn = ({ on, active, disabled, label, testid, children }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} disabled={disabled} title={label} aria-label={label}
    data-testid={`rte-${testid}`}
    className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors disabled:opacity-30 ${active ? 'bg-[#FF6FA8] text-[#081726]' : 'hover:bg-[var(--c-film)]'}`}>
    {children}
  </button>
);

function Toolbar({ editor }) {
  const c = () => editor.chain().focus();
  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link URL (leave empty to remove)', prev);
    if (url === null) return;
    if (!url.trim()) return c().extendMarkRange('link').unsetLink().run();
    const href = /^(https?:|mailto:)/i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
    c().extendMarkRange('link').setLink({ href }).run();
  };
  const I = 'w-4 h-4';
  return (
    <div className="flex flex-wrap gap-0.5 p-1 border-b border-[var(--c-hair)] sticky top-0 bg-regen-card z-10" data-testid="rte-toolbar">
      <Btn testid="bold" label="Bold" on={() => c().toggleBold().run()} active={editor.isActive('bold')}><Bold className={I} /></Btn>
      <Btn testid="italic" label="Italic" on={() => c().toggleItalic().run()} active={editor.isActive('italic')}><Italic className={I} /></Btn>
      <Btn testid="paragraph" label="Paragraph" on={() => c().setParagraph().run()} active={editor.isActive('paragraph')}><Pilcrow className={I} /></Btn>
      <Btn testid="h1" label="Heading 1" on={() => c().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })}><Heading1 className={I} /></Btn>
      <Btn testid="h2" label="Heading 2" on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })}><Heading2 className={I} /></Btn>
      <Btn testid="h3" label="Heading 3" on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })}><Heading3 className={I} /></Btn>
      <Btn testid="bullet" label="Bulleted list" on={() => c().toggleBulletList().run()} active={editor.isActive('bulletList')}><List className={I} /></Btn>
      <Btn testid="ordered" label="Numbered list" on={() => c().toggleOrderedList().run()} active={editor.isActive('orderedList')}><ListOrdered className={I} /></Btn>
      <Btn testid="hr" label="Divider" on={() => c().setHorizontalRule().run()}><Minus className={I} /></Btn>
      <Btn testid="link" label="Link" on={setLink} active={editor.isActive('link')}><Link2 className={I} /></Btn>
      <Btn testid="undo" label="Undo" on={() => c().undo().run()} disabled={!editor.can().undo()}><Undo2 className={I} /></Btn>
      <Btn testid="redo" label="Redo" on={() => c().redo().run()} disabled={!editor.can().redo()}><Redo2 className={I} /></Btn>
    </div>
  );
}

export const RichTextEditor = ({ initialContent, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, code: false, codeBlock: false, blockquote: false, strike: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
    ],
    content: initialContent || '',
    editorProps: { attributes: { class: 'rt-content min-h-[220px] p-3 outline-none', 'data-testid': 'article-body' } },
    onUpdate: ({ editor: e }) => onChange({ html: e.getHTML(), json: e.getJSON(), text: e.getText({ blockSeparator: '\n' }).replace(/\n{2,}/g, '\n').trim() }),
  });
  if (!editor) return null;
  return (
    <div className="rounded-md border border-[var(--c-hair)] overflow-hidden" data-testid="article-body-editor">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};
